import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Package, Loader2, RefreshCw, Trash2, Save, Archive, ArchiveRestore, Radar, ArrowRight, Layers, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { SEV_STYLE, timeAgo } from '@/lib/sentinelConstants';
import ProcessHelp from '@/components/ProcessHelp';

const CATEGORIES = [
  { v: 'general', l: 'General' },
  { v: 'risk', l: 'Risk' },
  { v: 'compliance', l: 'Compliance' },
  { v: 'operations', l: 'Operations' },
  { v: 'financial', l: 'Financial' },
];
const catLabel = (v) => (CATEGORIES.find((c) => c.v === v) || { l: 'General' }).l;

function BulkStampDialog({ pack, onClose, onDone }) {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [measures, setMeasures] = useState([]);
  const [selected, setSelected] = useState([]);
  const [prefix, setPrefix] = useState(pack ? pack.name : '');
  const [loadingM, setLoadingM] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.getSemanticModels().then((r) => setModels(r.models || r || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!modelId) { setMeasures([]); setSelected([]); return; }
    setLoadingM(true);
    api.getSentinelOptions(modelId)
      .then((o) => setMeasures(o.measures || []))
      .catch(() => setMeasures([]))
      .finally(() => setLoadingM(false));
    setSelected([]);
  }, [modelId]);

  const toggle = (name) => setSelected((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  const stamp = async () => {
    if (!modelId || !selected.length) return;
    setBusy(true);
    const targets = selected.map((name) => {
      const m = measures.find((x) => x.name === name) || {};
      return {
        model_id: modelId, metric: name, metric_label: m.label || name,
        name: `${(prefix || pack.name).trim()}: ${m.label || name}`, series_field: '', date_field: '',
      };
    });
    try {
      const r = await api.applySentinelPackBulk(pack.pack_id, targets);
      const failed = (r.errors || []).length;
      toast.success(`Stamped ${r.count} Sentinel${r.count === 1 ? '' : 's'}${failed ? ` · ${failed} skipped` : ''}`);
      onDone();
    } catch (e) { toast.error(e.message || 'Bulk stamp failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!pack} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="bulk-stamp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="w-4 h-4" /> Bulk stamp “{pack?.name}”</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Pick a model, then choose the metrics to apply this pack to. One draft Sentinel is created per metric.</p>
          <div>
            <label className="text-[11px] uppercase text-muted-foreground">Model</label>
            <Select value={modelId} onValueChange={setModelId}>
              <SelectTrigger className="h-9 mt-1" data-testid="bulk-stamp-model"><SelectValue placeholder="Choose a model" /></SelectTrigger>
              <SelectContent>{models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name || m.model_id}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase text-muted-foreground">Name prefix</label>
            <Input value={prefix} onChange={(e) => setPrefix(e.target.value)} className="mt-1" data-testid="bulk-stamp-prefix" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-muted-foreground">Metrics {selected.length ? `· ${selected.length} selected` : ''}</label>
            <div className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-border/50 divide-y divide-border/40" data-testid="bulk-stamp-measures">
              {loadingM ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : !modelId ? (
                <div className="py-8 text-center text-xs text-muted-foreground">Choose a model to list its metrics.</div>
              ) : measures.length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">This model exposes no numeric measures.</div>
              ) : measures.map((m) => (
                <button key={m.name} onClick={() => toggle(m.name)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/40" data-testid={`bulk-stamp-measure-${m.name}`}>
                  <Checkbox checked={selected.includes(m.name)} className="pointer-events-none" />
                  <span className="text-sm truncate">{m.label || m.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="bulk-stamp-cancel">Cancel</Button>
          <Button size="sm" onClick={stamp} disabled={busy || !modelId || !selected.length} data-testid="bulk-stamp-apply">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Stamp ${selected.length || ''} Sentinel${selected.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PackLibraryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [packs, setPacks] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');
  const [filter, setFilter] = useState('all');
  const [bulkPack, setBulkPack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getSentinelPacks();
      const rows = r.packs || [];
      setPacks(rows);
      setDrafts(Object.fromEntries(rows.map((p) => [p.pack_id, { name: p.name, description: p.description || '' }])));
    } catch (e) { toast.error(e.message || 'Failed to load packs'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDraft = (id, patch) => setDrafts((dd) => ({ ...dd, [id]: { ...dd[id], ...patch } }));

  const save = async (p) => {
    const d = drafts[p.pack_id] || {};
    if (!d.name?.trim()) return toast.error('Name is required');
    setBusy(`save_${p.pack_id}`);
    try { await api.updateSentinelPack(p.pack_id, { name: d.name.trim(), description: d.description || '' }); toast.success('Pack updated'); load(); }
    catch (e) { toast.error(e.message || 'Update failed'); } finally { setBusy(''); }
  };
  const setCategory = async (p, category) => {
    setBusy(`cat_${p.pack_id}`);
    try { await api.updateSentinelPack(p.pack_id, { category }); load(); }
    catch (e) { toast.error(e.message || 'Update failed'); } finally { setBusy(''); }
  };
  const toggleStatus = async (p) => {
    const next = (p.status || 'active') === 'active' ? 'retired' : 'active';
    setBusy(`st_${p.pack_id}`);
    try { await api.updateSentinelPack(p.pack_id, { status: next }); toast.success(next === 'retired' ? 'Pack retired' : 'Pack reactivated'); load(); }
    catch (e) { toast.error(e.message || 'Update failed'); } finally { setBusy(''); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete pack “${p.name}”? This cannot be undone.`)) return;
    setBusy(`del_${p.pack_id}`);
    try { await api.deleteSentinelPack(p.pack_id); toast.success('Pack deleted'); load(); }
    catch (e) { toast.error(e.message || 'Delete failed'); setBusy(''); }
  };

  const shown = filter === 'all' ? packs : packs.filter((p) => (p.category || 'general') === filter);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="pack-library-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Layers className="w-6 h-6 text-primary" /> Sentinel Pack Library</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Reusable Sentinel templates — rename, describe, categorise, retire, and see how often each has been stamped onto a metric.
            Apply one from any Sentinels list via “New from pack”, or bulk-stamp it across many metrics here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ProcessHelp
            testId="packs-help"
            title="Sentinel Pack Library"
            section="sentinel-packs"
            intro="Packs are reusable templates of a tuned Sentinel — its detection, thresholds, materiality and recipients — that you stamp onto any metric."
            steps={[
              'Save a pack from a Sentinel\u2019s detail page ("Save as pack").',
              'Here you can rename, describe, categorise (general / risk / compliance…) or retire a pack — retired packs are hidden from pickers but not deleted.',
              'Each row shows how many times the pack has been stamped onto a metric.',
              '"New from pack" (on the Sentinels list) opens the builder pre-filled from a pack.',
              'Bulk stamp applies one pack across several metrics of a model at once, creating one draft Sentinel each.',
            ]}
          />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="packs-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/sentinels')} data-testid="packs-sentinels-link"><Radar className="w-4 h-4 mr-1.5" /> Sentinels</Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="packs-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : shown.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 py-16 text-center" data-testid="packs-empty">
          <Package className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">
            {packs.length ? 'No packs in this category.' : 'No packs yet. Open a tuned Sentinel and choose “Save as pack” to create a reusable template.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((p) => {
            const d = drafts[p.pack_id] || { name: p.name, description: p.description || '' };
            const retired = (p.status || 'active') === 'retired';
            const dirty = d.name !== p.name || (d.description || '') !== (p.description || '');
            return (
              <div key={p.pack_id} className={`rounded-xl border p-4 space-y-3 ${retired ? 'border-border/40 bg-muted/20 opacity-80' : 'border-border/60 bg-card/60'}`} data-testid={`pack-row-${p.pack_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={SEV_STYLE[p.base_severity] || SEV_STYLE.low}>{p.base_severity}</Badge>
                    <Badge variant="outline">{p.detection_count} method{p.detection_count === 1 ? '' : 's'}</Badge>
                    <Badge variant="outline" className="border-indigo-500/40 text-indigo-600 dark:text-indigo-300" data-testid={`pack-category-${p.pack_id}`}>{catLabel(p.category || 'general')}</Badge>
                    {retired
                      ? <Badge variant="outline" className="border-border/50 text-muted-foreground" data-testid={`pack-status-${p.pack_id}`}>Retired</Badge>
                      : <Badge variant="outline" className="border-emerald-500/40 text-emerald-600 dark:text-emerald-300" data-testid={`pack-status-${p.pack_id}`}>Active</Badge>}
                    <span className="text-xs text-muted-foreground" data-testid={`pack-usage-${p.pack_id}`}>Used {p.usage_count || 0}×</span>
                  </div>
                  <span className="text-xs text-muted-foreground">from {p.source_sentinel_name || '—'} · {p.created_by_name || '—'} · {timeAgo(p.created_at)}</span>
                </div>
                <Input value={d.name} onChange={(e) => setDraft(p.pack_id, { name: e.target.value })} data-testid={`pack-name-${p.pack_id}`} />
                <Textarea rows={2} value={d.description} placeholder="Describe when to use this pack…" onChange={(e) => setDraft(p.pack_id, { description: e.target.value })} data-testid={`pack-desc-${p.pack_id}`} />
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={p.category || 'general'} onValueChange={(v) => setCategory(p, v)} disabled={busy === `cat_${p.pack_id}`}>
                    <SelectTrigger className="w-32 h-8" data-testid={`pack-category-select-${p.pack_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" disabled={!dirty || busy === `save_${p.pack_id}`} onClick={() => save(p)} data-testid={`pack-save-${p.pack_id}`}>
                    {busy === `save_${p.pack_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === `st_${p.pack_id}`} onClick={() => toggleStatus(p)} data-testid={`pack-toggle-status-${p.pack_id}`}>
                    {busy === `st_${p.pack_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (retired ? <><ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Reactivate</> : <><Archive className="w-3.5 h-3.5 mr-1" /> Retire</>)}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-500" disabled={busy === `del_${p.pack_id}`} onClick={() => remove(p)} data-testid={`pack-delete-${p.pack_id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                  {!retired ? (
                    <div className="ml-auto flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setBulkPack(p)} data-testid={`pack-bulk-${p.pack_id}`}>
                        <Copy className="w-3.5 h-3.5 mr-1" /> Bulk stamp
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin/sentinels/new?pack=${encodeURIComponent(p.pack_id)}`)} data-testid={`pack-apply-${p.pack_id}`}>
                        Use <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {bulkPack ? <BulkStampDialog pack={bulkPack} onClose={() => setBulkPack(null)} onDone={() => { setBulkPack(null); load(); }} /> : null}
    </div>
  );
}
